import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { UserRole } from './core/enums/user-role.enum';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent)
  },
  {
    path: 'auth',
    loadComponent: () => import('./pages/payment-auth/payment-auth.component').then(m => m.PaymentAuthComponent)
  },
  {
    path: 'unauthorized',
    loadComponent: () => import('./pages/unauthorized/unauthorized.component').then(m => m.UnauthorizedComponent)
  },
  {
    path: '',
    loadComponent: () => import('./layouts/public-layout/public-layout.component').then(m => m.PublicLayoutComponent),
    children: [
      {
        path: 'components',
        loadComponent: () => import('./pages/components-showcase/components-showcase.component').then(m => m.ComponentsShowcaseComponent)
      },
      {
        path: 'checkout',
        loadComponent: () => import('./pages/checkout/checkout.component').then(m => m.CheckoutComponent)
      },
      {
        path: 'payment-confirmation',
        loadComponent: () => import('./pages/payment-confirmation/payment-confirmation.component').then(m => m.PaymentConfirmationComponent)
      },
      {
        path: 'payment-link/:paymentId',
        loadComponent: () => import('./pages/payment-link/payment-link.component').then(m => m.PaymentLinkComponent)
      },
      {
        path: '',
        redirectTo: '/login',
        pathMatch: 'full'
      }
    ]
  },
  {
    path: 'app',
    canActivate: [authGuard],
    loadComponent: () => import('./layouts/authenticated-layout/authenticated-layout.component').then(m => m.AuthenticatedLayoutComponent),
    children: [
      {
        path: 'orders',
        loadComponent: () => import('./pages/order-selector/order-selector.component').then(m => m.OrderSelectorComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer, UserRole.Customer] }
      },
      {
        path: 'components',
        loadComponent: () => import('./pages/components-showcase/components-showcase.component').then(m => m.ComponentsShowcaseComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer, UserRole.Customer] }
      },
      {
        path: 'ncf-sequences',
        loadComponent: () => import('./pages/ncf-sequences/ncf-sequences.component').then(m => m.NcfSequencesComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'invoices',
        loadComponent: () => import('./pages/invoices/invoices.component').then(m => m.InvoicesComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'users',
        loadComponent: () => import('./pages/users/users.component').then(m => m.UsersComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenant-orders',
        loadComponent: () => import('./pages/tenant-orders/tenant-orders.component').then(m => m.TenantOrdersComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer] }
      },
      {
        path: 'tenant-orders/create',
        loadComponent: () =>
          import('./pages/tenant-orders/tenant-orders-create.component').then(m => m.TenantOrdersCreateComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer] }
      },
      {
        path: 'admin-tenant-orders',
        loadComponent: () =>
          import('./pages/admin-tenant-orders/admin-tenant-orders.component').then(m => m.AdminTenantOrdersComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'admin-payment-links',
        loadComponent: () =>
          import('./pages/admin-payment-links/admin-payment-links.component').then(m => m.AdminPaymentLinksComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'admin-payment-links-list',
        loadComponent: () =>
          import('./pages/admin-payment-links-list/admin-payment-links-list.component').then(m => m.AdminPaymentLinksListComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'advanced-statistics',
        loadComponent: () =>
          import('./pages/advanced-statistics/advanced-statistics.component').then(m => m.AdvancedStatisticsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'audit-logs',
        loadComponent: () =>
          import('./pages/audit-logs/audit-logs.component').then(m => m.AuditLogsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'application-logs',
        loadComponent: () =>
          import('./pages/application-logs/application-logs.component').then(m => m.ApplicationLogsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'payment-platforms',
        loadComponent: () =>
          import('./pages/payment-platforms/payment-platforms.component').then(m => m.PaymentPlatformsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'payment-platforms/create',
        loadComponent: () =>
          import('./pages/payment-platforms/payment-platforms-form.component').then(m => m.PaymentPlatformsFormComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'payment-platforms/:id',
        loadComponent: () =>
          import('./pages/payment-platforms/payment-platforms-form.component').then(m => m.PaymentPlatformsFormComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'payment-platforms/:id/edit',
        loadComponent: () =>
          import('./pages/payment-platforms/payment-platforms-form.component').then(m => m.PaymentPlatformsFormComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenant-payment-platforms',
        loadComponent: () =>
          import('./pages/tenant-payment-platforms/tenant-payment-platforms.component').then(m => m.TenantPaymentPlatformsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenant-payment-platforms/create',
        loadComponent: () =>
          import('./pages/tenant-payment-platforms/tenant-payment-platforms-form.component').then(m => m.TenantPaymentPlatformsFormComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenant-payment-platforms/:id',
        loadComponent: () =>
          import('./pages/tenant-payment-platforms/tenant-payment-platforms-form.component').then(m => m.TenantPaymentPlatformsFormComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenant-payment-platforms/:id/edit',
        loadComponent: () =>
          import('./pages/tenant-payment-platforms/tenant-payment-platforms-form.component').then(m => m.TenantPaymentPlatformsFormComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'account',
        loadComponent: () =>
          import('./pages/account/account.component').then(m => m.AccountComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer, UserRole.Customer] }
      },
      {
        path: 'customer-orders',
        loadComponent: () =>
          import('./pages/customer-orders/customer-orders.component').then(m => m.CustomerOrdersComponent),
        data: { expectedRoles: [UserRole.Customer] }
      },
      {
        path: 'admin-guide',
        loadComponent: () => import('./pages/admin-guide/admin-guide.component').then(m => m.AdminGuideComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenant-api-documentation',
        loadComponent: () => import('./pages/tenant-api-documentation/tenant-api-documentation.component').then(m => m.TenantApiDocumentationComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer] }
      },
      {
        path: 'catalogs',
        loadComponent: () => import('./pages/catalogs/catalogs.component').then(m => m.CatalogsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager, UserRole.Viewer] }
      },
      {
        path: 'tenants',
        loadComponent: () => import('./pages/tenants/tenants.component').then(m => m.TenantsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: 'tenants/:id',
        loadComponent: () => import('./pages/tenants/tenants.component').then(m => m.TenantsComponent),
        data: { expectedRoles: [UserRole.Admin, UserRole.Manager] }
      },
      {
        path: '',
        redirectTo: '/app/account',
        pathMatch: 'full'
      }
    ]
  }
];
